import { faixasCrescimentoValidas } from "./calculo/faixasCrescimentoUtils";

/**
 * Validacao pura da camada opcional "meta do colaborador" (Task 4 do plano
 * de correcao Fase 0). Extraida do computed subEtapaValida do
 * ModalCadastroMeta para ser testada sem montar o wizard inteiro.
 *
 * O caso concreto que motiva esta task: um participante e adicionado a meta
 * depois que as metas ja foram calculadas (media historica). Sem esta
 * validacao ele fica sem meta em form.metasCalculadas, ninguem percebe, e na
 * apuracao ele bate a "meta zero" e ganha o bonus sempre — daqui o bloqueio 1
 * abaixo, que exige meta preenchida e maior que zero para TODO participante
 * selecionado, nao so para os que existiam quando a media foi calculada.
 */

/**
 * @param {Object} form - novaMetaForm do wizard (ModalCadastroMeta)
 * @returns {boolean} true se a camada da meta do colaborador esta completa
 * (ou se usaMetaColaborador esta desligado, caso em que nao ha nada a validar)
 */
export function metaColaboradorValida(form) {
  if (!form || !form.usaMetaColaborador) return true;

  return (
    todasMetasParticipantesPreenchidas(form) &&
    parametrosMediaHistoricaValidos(form) &&
    bonusColaboradorValido(form)
  );
}

// Bloqueio 1: todo participante selecionado precisa ter meta preenchida e
// maior que zero em form.metasCalculadas — inclusive quem foi adicionado
// depois do calculo original (participante sem entrada nenhuma tambem falha).
function todasMetasParticipantesPreenchidas(form) {
  const metas = form.metasCalculadas || {};
  const colaboradores = form.colaboradoresSelecionados || [];
  return colaboradores.every(c => {
    const registro = metas[c.id_colaborador];
    if (!registro || registro.meta === "" || registro.meta == null) return false;
    const valor = parseFloat(registro.meta);
    return !isNaN(valor) && valor > 0;
  });
}

// Bloqueio 2: origem Media historica exige ao menos um mes de referencia e
// piso maior que zero — e, quando o crescimento e Progressivo, faixas
// completas (contiguas, cobrindo do piso ao infinito — ver
// faixasCrescimentoUtils.pendenciasFaixasCrescimento).
function parametrosMediaHistoricaValidos(form) {
  if (Number(form.origemMeta) !== 2) return true;
  if ((form.mesesReferencia || []).length === 0) return false;
  if (!(parseFloat(form.valorMinimoMeta) > 0)) return false;
  if (Number(form.tipoCrescimentoMedia) === 2) {
    return faixasCrescimentoValidas(form.faixasCrescimento, form.valorMinimoMeta);
  }
  return true;
}

// Bloqueio 3: bonus do colaborador — percentual entre 1 e 100 quando a
// origem e "% sobre referencia", valor maior que zero quando e "Informado".
function bonusColaboradorValido(form) {
  if (Number(form.origemBonusColaborador) === 2) {
    const percentual = parseFloat(form.comissaoPercentual);
    return percentual >= 1 && percentual <= 100;
  }
  const valor = parseFloat(form.valorBonusColaborador);
  return valor > 0;
}

/**
 * Task 15 (correcao Fase 0): lista, em texto, o que falta preencher para
 * metaColaboradorValida virar true — os mesmos tres bloqueios acima, so que
 * como mensagens em vez de booleano. Nao decide nada sozinha (nao substitui
 * metaColaboradorValida, que continua sendo quem libera/bloqueia o Salvar);
 * serve so para o aviso neutro da tela, que explica ao usuario o que falta
 * antes mesmo de ele encostar em qualquer campo (os erros vermelhos dos
 * campos individuais so aparecem depois de tocados — ver SubEtapaCalculoModelo,
 * SeletorOrigemMeta e BonusComissaoReferencia).
 * @param {Object} form
 * @returns {string[]} pendencias, na ordem em que os campos aparecem na tela
 */
export function pendenciasMetaColaborador(form) {
  if (!form || !form.usaMetaColaborador) return [];

  const pendencias = [];

  // Espelha bonusColaboradorValido.
  if (Number(form.origemBonusColaborador) === 2) {
    const percentual = parseFloat(form.comissaoPercentual);
    if (!(percentual >= 1 && percentual <= 100)) {
      pendencias.push("percentual da comissão de referência");
    }
  } else if (!(parseFloat(form.valorBonusColaborador) > 0)) {
    pendencias.push("valor do bônus");
  }

  // Espelha parametrosMediaHistoricaValidos.
  if (Number(form.origemMeta) === 2) {
    if ((form.mesesReferencia || []).length === 0) pendencias.push("mês de referência");
    if (!(parseFloat(form.valorMinimoMeta) > 0)) pendencias.push("piso da meta");
    if (Number(form.tipoCrescimentoMedia) === 2 && !faixasCrescimentoValidas(form.faixasCrescimento, form.valorMinimoMeta)) {
      pendencias.push("faixas de crescimento");
    }
  }

  // Espelha todasMetasParticipantesPreenchidas.
  const metas = form.metasCalculadas || {};
  const nSemMeta = (form.colaboradoresSelecionados || []).filter(c => {
    const registro = metas[c.id_colaborador];
    if (!registro || registro.meta === "" || registro.meta == null) return true;
    const valor = parseFloat(registro.meta);
    return isNaN(valor) || valor <= 0;
  }).length;
  if (nSemMeta > 0) {
    pendencias.push(nSemMeta === 1 ? "meta de 1 participante" : `meta de ${nSemMeta} participantes`);
  }

  return pendencias;
}
